import type { TModal } from 'widgets/global/Modal';
import { submitFormJson } from 'widgets/toolbox/ajax';
import type { TLoyaltyPointsPayload, TLoyaltyPointsResponse } from 'widgets/cart/LoyaltyPointsCartPayment';

/**
 * @description LoyaltyPointsCartModal implementation
 * @param Modal Base modal widget for extending
 * @returns LoyaltyPointsCartModal class
 */
function LoyaltyPointsCartModalClassCreator(Modal: TModal) {
    /**
     * @category widgets
     * @subcategory cart
     * @class LoyaltyPointsCartModal
     * @augments Modal
     * @classdesc Modal dialog for selecting a loyalty reward amount on Cart and Checkout pages.
     * Renders a list of radio-button voucher options and posts the selected amount on Save.
     * On success, emits cart/order update events to refresh totals and closes the modal.
     */
    class LoyaltyPointsCartModal extends Modal {
        prefs() {
            return {
                endpointUrl: '',
                msgError: '',
                classesBusy: 'm-busy',
                ...super.prefs()
            };
        }

        save() {
            const checkedInput = this.ref('self').get()?.querySelector<HTMLInputElement>(
                'input[name="loyaltyPoints"]:checked'
            );

            if (!checkedInput) {
                return;
            }

            const points = Number(checkedInput.value);

            if (isNaN(points)) {
                return;
            }

            const url = this.prefs().endpointUrl;

            if (!url) {
                return;
            }

            const amount = checkedInput.dataset.amount || '';

            this.ref('self').addClass(this.prefs().classesBusy);

            submitFormJson(url, <TLoyaltyPointsPayload>{ points }, 'POST')
                .then((response: TLoyaltyPointsResponse) => {
                    this.onSaved(response, amount);
                })
                .catch(() => {
                    this.closeModal();
                    this.eventBus().emit('alert.show', { accessibilityAlert: this.prefs().msgError });
                })
                .finally(() => {
                    this.ref('self').removeClass(this.prefs().classesBusy);
                });
        }

        onSaved(response: TLoyaltyPointsResponse, amount: string) {
            if (response && response.redirectUrl) {
                window.location.assign(response.redirectUrl);

                return;
            }

            if (response && response.success === false) {
                this.closeModal();
                this.eventBus().emit('alert.show', {
                    accessibilityAlert: response.errorMessage || this.prefs().msgError
                });

                return;
            }

            this.closeModal();
            this.emit('saved', { amount });

            if (response && response.cart) {
                this.eventBus().emit('minicart.updated', response.cart);
            }

            if (response && response.order) {
                this.eventBus().emit('order.updated', response.order);
            }
        }
    }

    return LoyaltyPointsCartModal;
}

export type TLoyaltyPointsCartModal = ReturnType<typeof LoyaltyPointsCartModalClassCreator>;

export type TLoyaltyPointsCartModalInstance = InstanceType<TLoyaltyPointsCartModal>;

export default LoyaltyPointsCartModalClassCreator;
